import { useMemo } from "react";

import { Permission } from "~/models/dto";
import { prop } from "~/tools/prop";

import type { DashboardReadSchema } from "../../common/openapi";
import { usePermissions } from "../../common/use-permissions";
import { useUserProfile } from "../../common/use-user-profile";

type MinimalSchema = Pick<DashboardReadSchema, "ownerId" | "name">;

const useDashboardsPermissions = (): {
	hasCreateDashboardsPermission: boolean;
	hasUpdateDashboardsPermission: (schema: MinimalSchema) => boolean;
	hasDeleteDashboardsPermission: (schema: MinimalSchema) => boolean;
} => {
	const permissions = usePermissions();

	const [userId] = useUserProfile({
		select: prop("id"),
	});

	return useMemo(() => {
		if (userId == null || permissions == null) {
			return {
				hasCreateDashboardsPermission: false,
				hasUpdateDashboardsPermission: () => false,
				hasDeleteDashboardsPermission: () => false,
			};
		}

		return {
			hasCreateDashboardsPermission: permissions.hasSystemPermission(Permission.CreateDashboards),

			hasUpdateDashboardsPermission({ ownerId, name }) {
				return userId === ownerId && permissions.hasResourcePermission(name, Permission.UpdateDashboards);
			},

			hasDeleteDashboardsPermission({ ownerId, name }) {
				return userId === ownerId && permissions.hasResourcePermission(name, Permission.DeleteDashboards);
			},
		};
	}, [permissions, userId]);
};

export { useDashboardsPermissions };
